import { useCallback, useRef, useState } from 'react';
import { apiFetch } from '@/apiFetch';
import type { ChatAttachment } from './useSoteriaChat';

/** Mirrors the server's allowlist. Kept here so a file the assistant could not
 *  read is refused in the composer, before an upload the API would 415. */
const ALLOWED = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
]);
const MAX_BYTES = 10 * 1024 * 1024;

/** One file in the composer, from selection to sent. */
export interface PendingAttachment {
  /** Local key while uploading; replaced by the server id once stored. */
  key: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
  status: 'uploading' | 'ready' | 'error';
  /** Server id — present only once the upload has landed. */
  id?: string;
  error?: string;
  /** Object URL for an image preview, so the chip shows the picture immediately. */
  previewUrl?: string;
}

let counter = 0;
const nextKey = () => `pending-${++counter}-${Math.random().toString(36).slice(2, 8)}`;

/** Files staged in the composer for the next message.
 *
 * Uploading starts the moment a file is chosen, WHILE the advisor is still
 * typing — so pressing send does not stall on a 10MB transfer. The price of that
 * is a race: send can happen mid-upload. `waitForUploads` is what makes it safe,
 * and `send` awaits it, so a file is never silently dropped from the turn it was
 * attached to.
 */
export function useAttachmentUploads(sessionId: string | null) {
  const [pending, setPending] = useState<Array<PendingAttachment>>([]);
  // Kept in a ref as well: `waitForUploads` is called from a send handler that
  // closed over an older render's state, and it must see the CURRENT uploads.
  const inflight = useRef<Map<string, Promise<void>>>(new Map());
  // What actually landed, and the order the advisor picked it in. Both are refs
  // for the same reason `inflight` is, and it is not a nicety: a send handler
  // awaits `waitForUploads()` and then has to read the RESULT of those uploads.
  // Reading `pending` there returns the array from the render that created the
  // handler — captured BEFORE the upload finished — so a file attached and sent
  // in the same breath still reads as `status: 'uploading'` with no id, gets
  // filtered out, and is dropped from the very turn it was attached to. That is
  // the failure `waitForUploads` exists to prevent, and waiting alone does not
  // prevent it. `landed` is written by the upload itself, so it is current by
  // the time the wait resolves; `order` keeps selection order, since uploads
  // finish in size order rather than the order they were chosen.
  const landed = useRef<Map<string, ChatAttachment>>(new Map());
  const order = useRef<Array<string>>([]);

  const upload = useCallback(
    async (key: string, file: File, session: string) => {
      const body = new FormData();
      body.append('session_id', session);
      body.append('file', file);
      try {
        const res = await apiFetch('/soteria/attachments', {
          method: 'POST',
          body,
        });
        if (!res.ok) {
          // The API explains WHY for a rejected file (415/413); showing that
          // beats a generic failure the advisor cannot act on.
          let detail = `Upload failed (${res.status})`;
          try {
            const parsed = await res.json();
            if (parsed?.detail) detail = String(parsed.detail);
          } catch {
            /* keep the status-based message */
          }
          throw new Error(detail);
        }
        const stored: ChatAttachment = await res.json();
        // Record the server's own row, not a copy assembled from the File: the
        // filename and size it stored are what the transcript will show.
        landed.current.set(key, stored);
        setPending((prev) =>
          prev.map((p) =>
            p.key === key ? { ...p, status: 'ready', id: stored.id } : p,
          ),
        );
      } catch (err) {
        setPending((prev) =>
          prev.map((p) =>
            p.key === key
              ? { ...p, status: 'error', error: (err as Error).message }
              : p,
          ),
        );
      } finally {
        inflight.current.delete(key);
      }
    },
    [],
  );

  /** Stage files and start uploading them. Returns nothing — watch `pending`. */
  const add = useCallback(
    (files: Array<File>) => {
      if (!sessionId) return;
      for (const file of files) {
        const key = nextKey();
        order.current.push(key);
        const type = (file.type || '').split(';')[0].trim().toLowerCase();

        // Refuse locally what the server would refuse anyway, with the same
        // reason, so the advisor is not left waiting on a doomed upload.
        let rejection: string | null = null;
        if (!ALLOWED.has(type)) {
          rejection = 'Only images and PDFs can be attached.';
        } else if (file.size > MAX_BYTES) {
          rejection = `That file is ${(file.size / 1024 / 1024).toFixed(1)}MB; the limit is 10MB.`;
        }

        setPending((prev) => [
          ...prev,
          {
            key,
            filename: file.name || 'attachment',
            size_bytes: file.size,
            mime_type: type,
            status: rejection ? 'error' : 'uploading',
            error: rejection ?? undefined,
            previewUrl:
              !rejection && type.startsWith('image/')
                ? URL.createObjectURL(file)
                : undefined,
          },
        ]);

        if (!rejection) {
          const task = upload(key, file, sessionId);
          inflight.current.set(key, task);
        }
      }
    },
    [sessionId, upload],
  );

  const remove = useCallback((key: string) => {
    setPending((prev) => {
      const gone = prev.find((p) => p.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
    inflight.current.delete(key);
    landed.current.delete(key);
    order.current = order.current.filter((k) => k !== key);
  }, []);

  /** Resolve once every in-flight upload has settled (landed or failed). */
  const waitForUploads = useCallback(async () => {
    while (inflight.current.size) {
      await Promise.allSettled([...inflight.current.values()]);
    }
  }, []);

  /** Clear the tray, releasing any preview URLs. Called after a turn is sent. */
  const clear = useCallback(() => {
    setPending((prev) => {
      for (const p of prev) if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      return [];
    });
    inflight.current.clear();
    landed.current.clear();
    order.current = [];
  }, []);

  /**
   * Wait for every upload, then hand back what landed and empty the tray.
   *
   * One call instead of wait-filter-map-clear at each call site. That sequence
   * was written out in the page's send handler, and the side panel — the same
   * composer, the same hook — simply never grew it. Anything a surface has to
   * remember to repeat is something a surface will eventually forget, and this
   * one fails silently: the file uploads, the message sends, and the attachment
   * is just not on it.
   *
   * Returns in the order the advisor picked the files, not the order they
   * finished uploading. Files still transferring are waited for; files the
   * server rejected are simply absent.
   */
  const takeReady = useCallback(async (): Promise<Array<ChatAttachment>> => {
    await waitForUploads();
    const ready = order.current
      .map((key) => landed.current.get(key))
      .filter((a): a is ChatAttachment => a !== undefined);
    clear();
    return ready;
  }, [waitForUploads, clear]);

  return {
    pending,
    add,
    remove,
    clear,
    waitForUploads,
    takeReady,
    /** True while anything is still transferring — send waits on this. */
    uploading: pending.some((p) => p.status === 'uploading'),
  };
}
